import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyCategoryForm = {
  categoryTitle: '',
  sort_order: 0,
  is_active: true,
};

const emptyPlanForm = {
  category_id: '',
  title: '',
  subtitle: '',
  price: '',
  price_tax_label: '',
  priceSuffix: '',
  featuresText: '',
  sort_order: 0,
  is_active: true,
};

function normalizeFeatures(text) {
  return String(text || '')
    .split('\n')
    .map((l) => String(l || '').trim())
    .filter(Boolean);
}

function AdminSitePricingDetailsPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [categories, setCategories] = React.useState([]);
  const [plans, setPlans] = React.useState([]);

  const categoryDragIndexRef = React.useRef(-1);
  const planDragIndexRef = React.useRef(-1);

  const [selectedCategoryId, setSelectedCategoryId] = React.useState('');

  const [openCategoryModal, setOpenCategoryModal] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState(null);
  const [categoryForm, setCategoryForm] = React.useState(emptyCategoryForm);

  const [openPlanModal, setOpenPlanModal] = React.useState(false);
  const [editingPlan, setEditingPlan] = React.useState(null);
  const [planForm, setPlanForm] = React.useState(emptyPlanForm);

  const fetchAll = async () => {
    setError('');
    setLoading(true);
    try {
      const [catsRes, plansRes] = await Promise.all([
        pb.collection('site_pricing_detailed_categories').getList(1, 500, { sort: 'sort_order, categoryTitle' }),
        pb.collection('site_pricing_detailed_plans').getList(1, 2000, { sort: 'sort_order, title', expand: 'category_id' }),
      ]);
      const nextCats = catsRes?.items ?? [];
      const nextPlans = plansRes?.items ?? [];
      setCategories(nextCats);
      setPlans(nextPlans);

      if (!selectedCategoryId && nextCats.length) {
        setSelectedCategoryId(nextCats[0].id);
      }
      if (selectedCategoryId && !nextCats.some((c) => c.id === selectedCategoryId)) {
        setSelectedCategoryId(nextCats[0]?.id || '');
      }
    } catch (e) {
      setError(e?.message || 'Erreur chargement Tarifs · Détails');
      setCategories([]);
      setPlans([]);
      setSelectedCategoryId('');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      ...emptyCategoryForm,
      sort_order: categories.length ? Math.max(...categories.map((c) => Number(c.sort_order || 0))) + 1 : 0,
    });
    setOpenCategoryModal(true);
  };

  const openEditCategory = (c) => {
    setEditingCategory(c);
    setCategoryForm({
      categoryTitle: c?.categoryTitle || c?.category_title || '',
      sort_order: Number(c?.sort_order || 0),
      is_active: c?.is_active !== false,
    });
    setOpenCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setOpenCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm(emptyCategoryForm);
  };

  const saveCategory = async () => {
    setLoading(true);
    try {
      const payload = {
        categoryTitle: categoryForm.categoryTitle || '',
        sort_order: Number(categoryForm.sort_order || 0),
        is_active: !!categoryForm.is_active,
      };
      if (editingCategory?.id) {
        await pb.collection('site_pricing_detailed_categories').update(editingCategory.id, payload);
      } else {
        await pb.collection('site_pricing_detailed_categories').create(payload);
      }
      await fetchAll();
      closeCategoryModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    setLoading(true);
    try {
      await pb.collection('site_pricing_detailed_categories').delete(id);
      await fetchAll();
      closeCategoryModal();
    } catch (e) {
      alert(e?.message || 'Erreur suppression');
    } finally {
      setLoading(false);
    }
  };

  const openCreatePlan = () => {
    if (!selectedCategoryId) {
      alert('Créez / sélectionnez une catégorie avant.');
      return;
    }
    setEditingPlan(null);
    const catPlans = plans.filter((p) => p?.category_id === selectedCategoryId);
    setPlanForm({
      ...emptyPlanForm,
      category_id: selectedCategoryId,
      sort_order: catPlans.length ? Math.max(...catPlans.map((p) => Number(p.sort_order || 0))) + 1 : 0,
    });
    setOpenPlanModal(true);
  };

  const openEditPlan = (p) => {
    setEditingPlan(p);
    const feats = Array.isArray(p?.features) ? p.features : (p?.features ? p.features : []);
    setPlanForm({
      category_id: p?.category_id || selectedCategoryId || '',
      title: p?.title || '',
      subtitle: p?.subtitle || '',
      price: p?.price || '',
      price_tax_label: p?.price_tax_label || '',
      priceSuffix: p?.priceSuffix || p?.price_suffix || '',
      featuresText: Array.isArray(feats) ? feats.join('\n') : '',
      sort_order: Number(p?.sort_order || 0),
      is_active: p?.is_active !== false,
    });
    setOpenPlanModal(true);
  };

  const closePlanModal = () => {
    setOpenPlanModal(false);
    setEditingPlan(null);
    setPlanForm(emptyPlanForm);
  };

  const savePlan = async () => {
    setLoading(true);
    try {
      const payload = {
        category_id: planForm.category_id || selectedCategoryId || '',
        title: planForm.title || '',
        subtitle: planForm.subtitle || '',
        price: planForm.price || '',
        price_tax_label: planForm.price_tax_label || '',
        priceSuffix: planForm.priceSuffix || '',
        features: normalizeFeatures(planForm.featuresText),
        sort_order: Number(planForm.sort_order || 0),
        is_active: !!planForm.is_active,
      };
      if (editingPlan?.id) {
        await pb.collection('site_pricing_detailed_plans').update(editingPlan.id, payload);
      } else {
        await pb.collection('site_pricing_detailed_plans').create(payload);
      }
      await fetchAll();
      closePlanModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Supprimer cette carte ?')) return;
    setLoading(true);
    try {
      await pb.collection('site_pricing_detailed_plans').delete(id);
      await fetchAll();
      closePlanModal();
    } catch (e) {
      alert(e?.message || 'Erreur suppression');
    } finally {
      setLoading(false);
    }
  };

  const persistCategoryOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_pricing_detailed_categories').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre (catégories)');
    }
  };

  const persistPlanOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_pricing_detailed_plans').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre (cartes)');
    }
  };

  const onCategoryDragStart = (e, index) => {
    categoryDragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onCategoryDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = categoryDragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setCategories((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      categoryDragIndexRef.current = overIndex;
      return next;
    });
  };

  const onCategoryDrop = async () => {
    categoryDragIndexRef.current = -1;
    const ordered = categories.slice();
    await persistCategoryOrder(ordered);
    await fetchAll();
  };

  const onPlanDragStart = (e, index) => {
    planDragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onPlanDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = planDragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    const catId = selectedCategoryId;
    if (!catId) return;

    setPlans((all) => {
      const catPlans = all
        .filter((p) => p?.category_id === catId)
        .slice()
        .sort((a, b) => {
          const ao = Number(a?.sort_order || 0);
          const bo = Number(b?.sort_order || 0);
          if (ao !== bo) return ao - bo;
          return (a?.created || '').localeCompare(b?.created || '');
        });

      if (from < 0 || from >= catPlans.length || overIndex < 0 || overIndex >= catPlans.length) return all;

      const nextCat = catPlans.slice();
      const [moved] = nextCat.splice(from, 1);
      nextCat.splice(overIndex, 0, moved);
      planDragIndexRef.current = overIndex;

      const nextCatWithOrder = nextCat.map((p, idx) => ({ ...p, sort_order: idx + 1 }));
      const others = all.filter((p) => p?.category_id !== catId);
      return [...others, ...nextCatWithOrder];
    });
  };

  const onPlanDrop = async () => {
    planDragIndexRef.current = -1;
    const catId = selectedCategoryId;
    if (!catId) return;
    const ordered = plans
      .filter((p) => p?.category_id === catId)
      .slice()
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0));
    await persistPlanOrder(ordered);
    await fetchAll();
  };

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) || null;
  const selectedCategoryPlans = plans
    .filter((p) => p?.category_id === selectedCategoryId)
    .sort((a, b) => {
      const ao = Number(a?.sort_order || 0);
      const bo = Number(b?.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return (a?.created || '').localeCompare(b?.created || '');
    });

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Tarifs · Détails</h1>
          <p style={{ marginTop: 6 }}>Gérez l’accordéon “Explorez nos Offres Détaillées”.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button className="btn" onClick={openCreateCategory} disabled={loading}>Nouvelle catégorie</button>
          <button className="btn btn-solid" onClick={openCreatePlan} disabled={loading || !selectedCategoryId}>Nouvelle carte</button>
        </div>
      </div>

      <style>{`
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }

        .grid{ display:grid; grid-template-columns: 320px 1fr; gap: 12px; margin-top: 12px; }
        @media (max-width: 980px){ .grid{ grid-template-columns: 1fr; } }

        .panel{ border: 1px solid var(--admin-border); border-radius: 12px; background: linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2)); padding: 12px; }
        .list-item{ display:flex; gap: 10px; align-items: flex-start; padding: 10px; border-radius: 12px; border: 1px solid transparent; cursor: pointer; }
        .list-item:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); }
        .admin-shell.admin-light .list-item:hover{ background: rgba(2,6,23,0.06); }
        .list-item.active{ border-color: rgba(79,70,229,0.55); box-shadow: 0 0 0 3px rgba(79,70,229,0.14); }

        .drag-handle{ cursor: grab; user-select:none; display:inline-flex; align-items:center; gap:8px; }
        .drag-handle:active{ cursor: grabbing; }

        .pill{ font-size: .8rem; padding: 3px 8px; border-radius: 999px; border: 1px solid var(--admin-border); opacity: .9; }

        .card-grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
        .plan-card{ border: 1px solid var(--admin-border); border-radius: 12px; padding: 12px; background: var(--admin-panel); cursor: grab; }
        .plan-card:active{ cursor: grabbing; }
        .plan-card h3{ margin: 0; font-size: 1.05rem; }
        .plan-card .muted{ color: var(--admin-muted); font-size: .9rem; margin-top: 6px; }
        .plan-card .price{ font-weight: 800; margin-top: 8px; }
        .plan-card ul{ margin: 10px 0 0; padding-left: 18px; }

        .actions{ display:flex; gap: 8px; justify-content: flex-end; margin-top: 10px; }
        .linkbtn{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:6px 8px; border-radius: 10px; }
        .linkbtn:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); }
        .admin-shell.admin-light .linkbtn:hover{ background: rgba(2,6,23,0.06); }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 920px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field textarea, .field select{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 160px; resize: vertical; }
        .field input:focus, .field textarea:focus, .field select:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginTop: 10 }}>{error}</div>}
      {loading && <div style={{ opacity: 0.8, marginTop: 10 }}>Chargement…</div>}

      <div className="grid">
        <div className="panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 800 }}>Catégories</div>
            <span className="pill">{categories.length}</span>
          </div>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {categories.map((c, idx) => {
                const title = c?.categoryTitle || c?.category_title || '—';
                const active = c?.is_active !== false;
                const isActive = c.id === selectedCategoryId;
                return (
                  <div
                    key={c.id}
                    className={'list-item' + (isActive ? ' active' : '')}
                    onClick={() => setSelectedCategoryId(c.id)}
                    draggable
                    onDragStart={(e) => onCategoryDragStart(e, idx)}
                    onDragOver={(e) => onCategoryDragOver(e, idx)}
                    onDrop={(e) => { e.preventDefault(); onCategoryDrop(); }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="drag-handle" aria-label="Réordonner">
                        <span style={{ opacity: 0.75 }}>⠿</span>
                        <span style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
                      </div>
                      <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 4 }}>Ordre: {Number(c?.sort_order || 0)} · {active ? 'Actif' : 'Inactif'}</div>
                    </div>
                    <button className="linkbtn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditCategory(c); }} title="Modifier">✏️</button>
                    <button className="linkbtn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteCategory(c.id); }} title="Supprimer">🗑️</button>
                  </div>
                );
              })}
            {categories.length === 0 && (
              <div style={{ opacity: 0.8, padding: 8 }}>Aucune catégorie. Clique “Nouvelle catégorie”.</div>
            )}
          </div>
        </div>

        <div className="panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 800 }}>Cartes</div>
            <span className="pill">{selectedCategoryPlans.length}</span>
          </div>

          {!selectedCategory && (
            <div style={{ marginTop: 10, opacity: 0.8 }}>Sélectionne une catégorie.</div>
          )}

          {selectedCategory && (
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 10, color: 'var(--admin-muted)' }}>
                Catégorie sélectionnée: <strong style={{ color: 'var(--admin-text)' }}>{selectedCategory?.categoryTitle || selectedCategory?.category_title || '—'}</strong>
              </div>
              <div className="card-grid">
                {selectedCategoryPlans.map((p, idx) => {
                  const feats = Array.isArray(p?.features) ? p.features : (p?.features ? p.features : []);
                  const active = p?.is_active !== false;
                  return (
                    <div
                      key={p.id}
                      className="plan-card"
                      draggable
                      onDragStart={(e) => onPlanDragStart(e, idx)}
                      onDragOver={(e) => onPlanDragOver(e, idx)}
                      onDrop={(e) => { e.preventDefault(); onPlanDrop(); }}
                    >
                      <div className="drag-handle" aria-label="Réordonner">
                        <span style={{ opacity: 0.75 }}>⠿</span>
                        <h3>{p.title || '—'}</h3>
                      </div>
                      {p.subtitle && <div className="muted">{p.subtitle}</div>}
                      <div className="price">{p.price || '—'} {p.priceSuffix ? <span style={{ opacity: 0.8, fontWeight: 600 }}>{p.priceSuffix}</span> : null}</div>
                      {feats.length ? (
                        <ul>
                          {feats.slice(0, 6).map((f, idx) => (
                            <li key={idx}>{f}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="muted" style={{ marginTop: 10 }}>Aucune feature</div>
                      )}
                      <div className="actions">
                        <span className="pill">{active ? 'Actif' : 'Inactif'}</span>
                        <button className="linkbtn" onClick={() => openEditPlan(p)} title="Modifier">✏️</button>
                        <button className="linkbtn" onClick={() => deletePlan(p.id)} title="Supprimer">🗑️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedCategoryPlans.length === 0 && (
                <div style={{ marginTop: 10, opacity: 0.8 }}>Aucune carte pour cette catégorie. Clique “Nouvelle carte”.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {openCategoryModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target?.classList?.contains('modal-overlay')) closeCategoryModal();
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingCategory?.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
              <button className="close-btn" onClick={closeCategoryModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Titre</label>
                <input type="text" value={categoryForm.categoryTitle} onChange={(e) => setCategoryForm((f) => ({ ...f, categoryTitle: e.target.value }))} placeholder="ex: ☁️ Hébergement Web" />
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={categoryForm.sort_order} onChange={(e) => setCategoryForm((f) => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={categoryForm.is_active} onChange={(e) => setCategoryForm((f) => ({ ...f, is_active: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editingCategory?.id && (
                <button className="btn" onClick={() => deleteCategory(editingCategory.id)} disabled={loading}>Supprimer</button>
              )}
              <button className="btn" onClick={closeCategoryModal} disabled={loading}>Annuler</button>
              <button className="btn btn-solid" onClick={saveCategory} disabled={loading}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {openPlanModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target?.classList?.contains('modal-overlay')) closePlanModal();
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingPlan?.id ? 'Modifier la carte' : 'Nouvelle carte'}</h3>
              <button className="close-btn" onClick={closePlanModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label>Catégorie</label>
                <select value={planForm.category_id} onChange={(e) => setPlanForm((f) => ({ ...f, category_id: e.target.value }))}>
                  <option value="">(Sélectionner)</option>
                  {categories
                    .slice()
                    .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c?.categoryTitle || c?.category_title || '—'}</option>
                    ))}
                </select>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={planForm.sort_order} onChange={(e) => setPlanForm((f) => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Titre</label>
                <input type="text" value={planForm.title} onChange={(e) => setPlanForm((f) => ({ ...f, title: e.target.value }))} placeholder="ex: Basique" />
              </div>
              <div className="field full">
                <label>Sous-titre</label>
                <input type="text" value={planForm.subtitle} onChange={(e) => setPlanForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="ex: Hébergement avec support de base." />
              </div>
              <div className="field">
                <label>Prix</label>
                <input type="text" value={planForm.price} onChange={(e) => setPlanForm((f) => ({ ...f, price: e.target.value }))} placeholder="ex: À partir de €69" />
              </div>
              <div className="field">
                <label>HT / TTC</label>
                <select value={planForm.price_tax_label} onChange={(e) => setPlanForm((f) => ({ ...f, price_tax_label: e.target.value }))}>
                  <option value="">(Aucun)</option>
                  <option value="HT">HT</option>
                  <option value="TTC">TTC</option>
                </select>
              </div>
              <div className="field">
                <label>Suffixe prix</label>
                <input type="text" value={planForm.priceSuffix} onChange={(e) => setPlanForm((f) => ({ ...f, priceSuffix: e.target.value }))} placeholder="ex: /an" />
              </div>
              <div className="field full">
                <label>Features (1 par ligne)</label>
                <textarea value={planForm.featuresText} onChange={(e) => setPlanForm((f) => ({ ...f, featuresText: e.target.value }))} placeholder="Sauvegardes automatiques (hebdomadaires)\nCertificat SSL inclus\n…" />
                <small>Astuce: tu peux mettre ✓/✗, ou du texte libre.</small>
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={planForm.is_active} onChange={(e) => setPlanForm((f) => ({ ...f, is_active: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editingPlan?.id && (
                <button className="btn" onClick={() => deletePlan(editingPlan.id)} disabled={loading}>Supprimer</button>
              )}
              <button className="btn" onClick={closePlanModal} disabled={loading}>Annuler</button>
              <button className="btn btn-solid" onClick={savePlan} disabled={loading}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSitePricingDetailsPage;
